import React from 'react';
import type { Course, BudgetResult, RecoveryPath } from '@abs/shared-types';
import { useAppStore } from '@/store';
import { AbsenceType } from '@abs/shared-types';
import { todayISO } from '@abs/shared-logic';
import { BudgetGauge, RiskBadge, PenaltyCountdown, Button } from '../shared';

export interface CourseCardProps {
  course: Course;
  budget: BudgetResult;
  recovery: RecoveryPath | null;
}

export function CourseCard({ course, budget }: CourseCardProps): React.ReactElement {
  const selectCourse = useAppStore((s) => s.selectCourse);
  const logAbsence = useAppStore((s) => s.logAbsence);

  const handleQuickLog = (e: React.MouseEvent) => {
    e.stopPropagation();
    logAbsence({
      courseId: course.id,
      date: todayISO(),
      type: AbsenceType.Unexcused,
      reason: null,
      arrivalTime: null,
      departureTime: null,
      note: '',
    });
  };

  return (
    <div
      className="course-card"
      onClick={() => selectCourse(course.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectCourse(course.id); } }}
      aria-label={`${course.code} — ${course.name}`}
    >
      <div className="course-card__header">
        <h3 className="course-card__name">{course.name}</h3>
        <span className="course-card__code">{course.code}</span>
      </div>

      <BudgetGauge
        used={budget.usedTotal}
        total={budget.totalAllowed}
        riskLevel={budget.riskLevel}
      />

      <div className="course-card__status">
        <RiskBadge level={budget.riskLevel} size="sm" />
        <PenaltyCountdown
          absencesToNextTier={budget.absencesToNextTier}
          nextTier={budget.nextTier}
        />
      </div>

      <div className="course-card__actions">
        <Button variant="secondary" size="sm" onClick={handleQuickLog}>
          Log Absence
        </Button>
      </div>
    </div>
  );
}
